import { api } from '../../services/apiService';
import { isNativeCapacitorRuntime } from '../../services/revenueCatService';
import {
  buildAbsiteMobilePaywallPlans,
  isMobileDevPaywallEnabled,
  MOBILE_DEV_PAYWALL_ABSITE_PLAN,
} from '../../constants/mobileBilling';
import {
  getAbsiteAppleTrialEligibility,
  getAbsiteMobilePackagePricing,
} from '../../services/mobileBillingService';
import { trialDaysFromPlans, trialPlanIdFromPlans } from './daily-limit';

// The offer must not hold the card or the modal up for long. Past this, no trial is promised.
export const TRIAL_CHECK_TIMEOUT_MS = 5000;

/**
 * Whether to offer "Try Pro free for N days", and N. Mirrors the paywall's own
 * checks (subscriptions-list.jsx), but fails closed everywhere: this surface
 * promises a trial BEFORE the paywall re-checks, so an unknown answer is a "no".
 *
 * - Web: GET /user/{id}/trial-eligible must say exactly true (the server's rule:
 *   the account never held a non-FREE plan), and the paid plans with a trial
 *   must agree on one length (trialDaysFromPlans).
 * - Native (the App Store build, or the local dev paywall): the same plan the
 *   native paywall sells (buildAbsiteMobilePaywallPlans; none means no trial),
 *   whose trialPeriodDays must not be 0 (subscriptions-list hasNativeTrial),
 *   Apple's intro-offer eligibility for its product AND the server's, and the
 *   length is that StoreKit package's free intro offer (runbook MUST-a, #541):
 *   never the backend's web trialPeriodDays.
 *
 * Never throws. Resolves { days, native, planId? }; days is null when no trial is
 * offered. planId (web only) is the single trial plan to open at checkout, or null.
 */
export const resolveTrialOffer = async ({
  userId,
  exam = null,
  native = isNativeCapacitorRuntime() || isMobileDevPaywallEnabled(),
  client = api,
  appleEligibility = getAbsiteAppleTrialEligibility,
  storePricing = getAbsiteMobilePackagePricing,
  timeoutMs = TRIAL_CHECK_TIMEOUT_MS,
} = {}) => {
  const none = { days: null, native };
  if (!userId) {
    return none;
  }

  const check = async () => {
    try {
      if (native) {
        const devPaywall = isMobileDevPaywallEnabled();
        // The plan the native paywall would show (subscriptions.jsx availablePlans).
        const [plan] = devPaywall
          ? [MOBILE_DEV_PAYWALL_ABSITE_PLAN]
          : buildAbsiteMobilePaywallPlans(await client.getAllPlans());
        if (!plan?.revenueCatProductId || plan.trialPeriodDays === 0) {
          return none;
        }
        const productId = plan.revenueCatProductId;
        // The dev paywall has no backend user; the paywall skips the server check there too.
        const serverEligible = devPaywall
          ? Promise.resolve(true)
          : Promise.resolve(client.getTrialEligibility(userId)).then((res) => res === true);
        const [apple, server, pricing] = await Promise.all([
          appleEligibility({ appUserID: userId, productId }),
          serverEligible,
          storePricing({ appUserID: userId, productId }),
        ]);
        const days = Number(pricing?.introTrialDays);
        return apple === true && server === true && pricing?.ready === true && Number.isInteger(days) && days > 0
          ? { days, native }
          : none;
      }
      const [eligible, plans] = await Promise.all([
        client.getTrialEligibility(userId),
        client.getAllPlans(),
      ]);
      if (eligible !== true) {
        return none;
      }
      const days = trialDaysFromPlans(plans, exam);
      // The plan to open at checkout; only when there is a single trial length to promise.
      return { days, native, planId: days === null ? null : trialPlanIdFromPlans(plans, exam) };
    } catch {
      return none;
    }
  };

  let timer;
  const timeout = new Promise((resolve) => {
    timer = setTimeout(() => resolve(none), timeoutMs);
  });
  try {
    return await Promise.race([check(), timeout]);
  } finally {
    clearTimeout(timer);
  }
};
